import React from 'react';
import {Field, useForm} from 'react-final-form';
import {FormFieldRadioButton} from '@yandex-data-ui/cloud-components';

import {ComputeFormFieldsValues} from 'components/calculator/ComputeFormFields/ComputeFormFields';
import CalculatorFormLayout from 'components/calculator/CalculatorFormLayout/CalculatorFormLayout';
import {
    findClosestValue,
    getGpuCoresOptions,
    getPlatformMemoryVariants,
} from 'components/calculator/ComputeFormFields/utils';
import {ComputeConfig} from 'components/calculator/models';
import withTranslation, {WithTranslationProps} from 'hoc/withTranslation';

interface GPUCoresFieldProps extends WithTranslationProps {
    name: string;
    platforms: ComputeConfig['platforms'];
    values: ComputeFormFieldsValues;
}

const GPUCoresField: React.FC<GPUCoresFieldProps> = ({name, platforms, values, i18nK}) => {
    const {change} = useForm<ComputeFormFieldsValues>();

    const handleGpuCoresChange = React.useCallback(
        (newGpuCores: string) => {
            const {platform, cores, memory} = values;
            const platformData = platforms.find(({id}) => id === platform);
            let newCores = cores;

            if (!platformData) {
                return;
            }
            const gpuConfiguration =
                platformData.allowedGpuConfigurations &&
                platformData.allowedGpuConfigurations.find(
                    ({gpus}) => Number(gpus) === Number(newGpuCores),
                );

            if (gpuConfiguration && gpuConfiguration.cores) {
                newCores = gpuConfiguration.cores;
                change(`${name}cores` as keyof ComputeFormFieldsValues, newCores);
            }

            const currentPlatformMemoryVariants = getPlatformMemoryVariants(
                platformData,
                100,
                newCores,
            );

            if (!currentPlatformMemoryVariants.includes(memory)) {
                const newMemory = findClosestValue(currentPlatformMemoryVariants, memory);
                change(`${name}memory` as keyof ComputeFormFieldsValues, newMemory);
            }
        },
        [values, platforms, change, name],
    );

    return (
        <CalculatorFormLayout.Row
            title={i18nK('field_gpu-cores')}
            note={i18nK('context_gpu-cores')}
        >
            <Field name={`${name}gpuCores`} type="radio">
                {(fieldProps) => (
                    <FormFieldRadioButton
                        {...fieldProps}
                        size="m"
                        options={getGpuCoresOptions(platforms, values.platform)}
                        width="max"
                        onUpdate={handleGpuCoresChange}
                    />
                )}
            </Field>
        </CalculatorFormLayout.Row>
    );
};

export default withTranslation('calculator')(GPUCoresField);
